import { AddIcon, ChevronRightIcon, EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Switch,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { TicketLabel, TicketPriority } from "../types/ticket.types";
import ClientSideTable from "../components/table/ClientSideTable";
import PrimaryButton from "../components/PrimaryButton";
import PenIcon from "../components/icons/PenIcon";
import CreateLabelModal from "../components/modals/CreateLabelModal";
import { useTicketLabels } from "../hooks/ticket_labels.hooks";
import TrashIcon from "../components/icons/TrashIcon";
import { useMutation, useQueryClient } from "react-query";
import {
  deleteTicketLabel,
  updateTicketLabel,
} from "../mutations/ticket_label.mutations";
import { useConnectedUser } from "../hooks/user.hooks";
import { useNavigate } from "react-router-dom";

function TicketingCreateLabelsPage() {
  const user = useConnectedUser()
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [labelSelected, setLabelSelected] = useState<TicketLabel | null>(null);
  const queryClient = useQueryClient();
  const { mutate: mutateDeleteTicketLabel } = useMutation(deleteTicketLabel, {
    onSuccess: () => {
      queryClient.invalidateQueries("ticket_labels");
    },
  });
  const { mutate: mutateUpdateTicketLabel } = useMutation(updateTicketLabel, {
    onSuccess: () => {
      queryClient.invalidateQueries("ticket_labels");
    },
  });
  const columns = useMemo<ColumnDef<TicketLabel>[]>(
    () => [
      {
        accessorFn: (row) => row.label,
        id: "name",
        header: "Nom du motif",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <Badge
            h="1.5rem"
            lineHeight="1.5rem"
            px="1rem"
            rounded="lg"
            border={"solid 1px"}
            borderColor={"brand.200"}
            color={"brand.700"}
            textTransform={"capitalize"}
            w="fit-content"
            backgroundColor={"brand.50"}
          >
            {row.discount}
            {"%"}
          </Badge>
        ),
        id: "discount",
        header: "Remise",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <Badge
            h="1.5rem"
            lineHeight="1.5rem"
            px="1rem"
            rounded="md"
            border={"solid 1px"}
            borderColor={"gray.100"}
            color={"gray.700"}
            textTransform={"capitalize"}
            w="fit-content"
            backgroundColor={"white"}
          >
            {row.proof ? "Oui" : "Non"}
          </Badge>
        ),
        id: "prouf",
        header: "Preuve",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => {
          switch (row.priority) {
            case TicketPriority.LOW:
              return (
                <Badge
                  h="1.5rem"
                  lineHeight="1.5rem"
                  px="1rem"
                  rounded="md"
                  border={"solid 1px"}
                  borderColor={"green.100"}
                  color={"green.700"}
                  textTransform={"capitalize"}
                  w="fit-content"
                  backgroundColor={"green.50"}
                >
                  Normale
                </Badge>
              );
            case TicketPriority.MEDIUM:
              return (
                <Badge
                  h="1.5rem"
                  lineHeight="1.5rem"
                  px="1rem"
                  rounded="md"
                  border={"solid 1px"}
                  borderColor={"orange.100"}
                  color={"orange.700"}
                  textTransform={"capitalize"}
                  w="fit-content"
                  backgroundColor={"orange.50"}
                >
                  Moyenne
                </Badge>
              );
            case TicketPriority.HIGH:
              return (
                <Badge
                  h="1.5rem"
                  lineHeight="1.5rem"
                  px="1rem"
                  rounded="md"
                  border={"solid 1px"}
                  borderColor={"red.100"}
                  color={"red.700"}
                  textTransform={"capitalize"}
                  w="fit-content"
                  backgroundColor={"red.50"}
                >
                  Haute
                </Badge>
              );
            default:
              return row.priority;
          }
        },
        id: "priority",
        header: "Priorité",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },

      {
        accessorFn: (row) => (
          <>
            <Switch
              isChecked={row.published}
              mr={4}
              onChange={() =>
                mutateUpdateTicketLabel({
                  id: row.id,
                  published: !row.published,
                })
              }
            ></Switch>
            <EditIcon
              w="24px"
              h="24px"
              color="gray.500"
              mr={4}
              onClick={() => {
                setLabelSelected(row);
                onOpen();
              }}
            ></EditIcon>
            <TrashIcon
              w="24px"
              h="24px"
              color="gray.500"
              mr={4}
              onClick={() => mutateDeleteTicketLabel(row.id)}
            ></TrashIcon>
          </>
        ),
        id: "actions",
        header: "",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
    ],
    []
  );
  const { data: rawData } = useTicketLabels({ all: true });
  const data = useMemo(() => rawData ?? [], [rawData]);
  const table = useReactTable({
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const handleOnNew = useCallback(() => {
    onOpen();
  }, [onOpen]);
  const handleOnClose = useCallback(() => {
    onClose();
    setLabelSelected(null);
  }, [onClose]);
  const TicketingCreateLabelsPageNoDataComponent = () => {
    return (
      <Flex w="100%" justifyContent="center">
        <Flex
          w="100%"
          rounded="xl"
          bg="white"
          textAlign="center"
          lineHeight="44px"
          color="gray.500"
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          py="100px"
        >
          <Flex
            w="48px"
            h="48px"
            bg="zinc.50"
            border="solid 1px"
            borderColor="brand.100"
            justifyContent="center"
            alignItems="center"
            borderRadius={100}
            mr="8px"
          >
            <PenIcon w="24px" h="24px" color="brand.700"></PenIcon>
          </Flex>
          <Text
            mt={4}
            color={"gray.900"}
            fontSize={"20px"}
            lineHeight={"30px"}
            fontWeight={600}
          >
            Aucun labels
          </Text>
          <PrimaryButton mt={4} mb={4} w={"30rem"} onClick={handleOnNew}>
            Créer un label
          </PrimaryButton>
        </Flex>
      </Flex>
    );
  };
  const TicketingCreateLabelsPageActionsComponent = () => {
    return (
      <>
        <PrimaryButton w={"fit-content"} onClick={handleOnNew}>
          <AddIcon mr={4}></AddIcon>
          Ajouter un libellé
        </PrimaryButton>
      </>
    );
  };
  if (!user?.isSuperAdmin) {
    navigate(-1);
    return;
  }
  return (
    <>
      <Breadcrumb
        spacing="8px"
        separator={<ChevronRightIcon color="gray.500" />}
      >
        <BreadcrumbItem>
          <BreadcrumbLink
            href="/tickets"
            color="gray.600"
            fontWeight={600}
            fontSize="14px"
          >
            Tickets SAV / Assistance
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <BreadcrumbLink
            href="#"
            color="brand.700"
            fontWeight={600}
            fontSize="14px"
          >
            Gérer les libellés
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <PageTitle>Gérer les libellés</PageTitle>

      <ClientSideTable
        table={table}
        w={["auto", "10rem", "10rem", "10rem", "10rem"]}
        flexGrow={[1, 0, 0, 0, 0]}
        noDataComponent={TicketingCreateLabelsPageNoDataComponent}
        actionsComponent={TicketingCreateLabelsPageActionsComponent}
      ></ClientSideTable>

      <CreateLabelModal
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={handleOnClose}
        defaultValue={labelSelected}
      ></CreateLabelModal>
    </>
  );
}

export default TicketingCreateLabelsPage;
